import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import useTsgTrigger from "../hooks/useTsgTrigger";
import useSkillGenieSession from "../hooks/useSkillGenieSession";
import TSGLanguageSelector from "../components/TSGLanguageSelector";
import TSGCommandPalette from "../components/TSGCommandPalette";
import { Mic } from "lucide-react";
import { useTenant } from "../Context/TenantContext";
import PremiumUpgradeModal from "../components/PremiumUpgradeModal";
import { useSubscription } from "../Context/SubscriptionContext";
import FeatureGuard from "../components/access/FeatureGuard";
import API_BASE_URL from "../Config/config";
import axios from "axios";

const NotesPage = () => {
 return (
  <FeatureGuard feature="TSG_NOTES">
    <div className="min-h-screen bg-[#F8FAFC] text-body px-4 py-6 sm:px-6 md:px-12 lg:px-32 font-sans transition-all duration-300 ease-in-out relative overflow-hidden">
        <NotesContent />
    </div>
  </FeatureGuard>
 );
};

const NotesContent = () => {
  const textareaRef = useRef(null);
  const [showPalette, setShowPalette] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const { session, updateSession } = useSkillGenieSession();
  const { subscription } = useSubscription();
  const navigate = useNavigate();

  const storedUser = localStorage.getItem("user");
  let userId = null;
  try {
    const parsedUser = storedUser ? JSON.parse(storedUser) : null;
    userId = parsedUser?.userId || parsedUser?.id || parsedUser?.user_id;
  } catch (err) {
    console.error("❌ Failed to parse user:", err);
  }

  const handleSave = async () => {
    if (!userId) {
      alert("Please log in to save notes.");
      return;
    }
    const content = textareaRef.current?.value;
    if (!content || !content.trim()) {
      alert("Note content cannot be empty.");
      return;
    }

    setIsSaving(true);
    setSaveStatus("Saving...");

    try {
      const noteData = {
        notes: [
          {
            id: crypto.randomUUID(),
            title: content.substring(0, 30) + (content.length > 30 ? "..." : ""),
            content: content,
          },
        ],
      };

      await axios.post(`${API_BASE_URL}/notes/${userId}`, noteData, {
        headers: { "user-id": userId },
      });

      setSaveStatus("✅ Saved!");
      setTimeout(() => setSaveStatus(""), 3000);
    } catch (err) {
      console.error("❌ Failed to save note:", err);
      setSaveStatus("❌ Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  useTsgTrigger(textareaRef, () => {
 console.log("🔥 @tsg triggered");
 setShowPalette(true);
 });

 const handleCommandSelect = (cmd) => {
 setShowPalette(false);
 navigate(`/tsg/${cmd.intent}`, {
 state: {
 fromNotes: true,
 promptIntent: cmd.intent,
 language: session?.language ||"en",
 },
 });
 };

 return (
    <>
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl -ml-48 -mb-48"></div>
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-gray-100 pb-8 mb-10 relative z-10">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate(-1)}
              className="w-12 h-12 bg-white flex items-center justify-center rounded-2xl shadow-sm border border-gray-100 hover:bg-purple-50 hover:border-[#5B21B6] transition-all group"
              title="Go Back"
            >
              <ChevronLeftIcon className="w-6 h-6 text-[#5B21B6] group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="flex items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-purple-200 p-3 mr-2">
                <svg className="w-full h-full text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-black text-[#111827] tracking-tight">
                  Smart Notes
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] font-black text-[#5B21B6] uppercase tracking-[0.2em]">AI Powered Engine</span>
                  {subscription.isPaid && subscription.daysRemaining !== null && (
                    <div className="flex items-center gap-1.5 bg-green-50 px-3 py-1 rounded-full border border-green-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-green-600 text-[9px] font-black uppercase tracking-tight">
                        {subscription.daysRemaining} days left
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Language selector + Save */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-2xl border border-gray-100 shadow-sm transition-all focus-within:ring-4 focus-within:ring-purple-50">
              <span className="text-lg">🌐</span>
              <TSGLanguageSelector
                selectedLanguage={session.language}
                onChange={(lang) => updateSession({ language: lang })}
                className="bg-transparent text-[#111827] text-[10px] font-black uppercase tracking-widest focus:outline-none cursor-pointer"
              />
            </div>

            <button
              className="bg-[#5B21B6] text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-[0_15px_30px_rgba(91,33,182,0.2)] hover:bg-[#7C3AED] hover:-translate-y-1 transition-all flex items-center gap-2 disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Note"}
            </button>
            {saveStatus && (
              <span className={`text-[10px] font-bold ${saveStatus.includes("✅") ? "text-green-600" : "text-red-600"}`}>
                {saveStatus}
              </span>
            )}
          </div>
        </div>

        {/* Textarea Container */}
        <div className="relative rounded-[32px] interactive-card bg-white p-0 border-none shadow-[0_40px_80px_rgba(91,33,182,0.1)] focus-within:ring-[12px] focus-within:ring-purple-50 transition-all overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#5B21B6] via-[#7C3AED] to-[#A855F7]"></div>
          
          <textarea
            ref={textareaRef}
            placeholder="Type @tsg to activate your AI companion..."
            className="w-full h-[300px] sm:h-[500px] p-10 text-xl font-medium bg-transparent text-[#111827] placeholder-gray-200 focus:outline-none resize-none leading-relaxed custom-scrollbar"
          />
        
        {showPalette && (
        <TSGCommandPalette
        open={showPalette}
        onClose={() => setShowPalette(false)}
        onSelect={handleCommandSelect}
        />
        )}
        </div>

        {/* Helper info */}
        <div className="mt-4 flex items-center justify-center gap-3 text-gray-400 text-[10px] font-black uppercase tracking-widest">
          <span className="w-2 h-2 rounded-full bg-[#5B21B6] animate-ping"></span>
          Pro Tip: Type <code className="text-[#5B21B6] font-black px-2 py-0.5 bg-purple-50 rounded border border-purple-100 mx-1">@tsg</code> anywhere to summon AI help
        </div>

        {/* ✅ Voice Assistant Bubble — only visible while palette is open */}
        {showPalette && (
        <button
          onClick={() => navigate("/voiceassistant")}
          className="fixed bottom-10 right-10 w-20 h-20 rounded-full bg-gradient-to-tr from-[#5B21B6] via-[#7C3AED] to-[#A855F7] hover:scale-110 flex items-center justify-center shadow-[0_20px_40px_rgba(91,33,182,0.4)] transition-all animate-pulse z-[9999] border-4 border-white"
          title="Voice Assistant"
        >
          <Mic className="w-10 h-10 text-white" />
        </button>
        )}
    </>
 );
}

export default NotesPage;




